import * as XLSX from 'xlsx-js-style';
import { applyWorkbookRtl, AMOUNT_NUMBER_FORMAT, REPORT_STYLES } from './ExcelReport';
import { beautifyNumber } from './FormatNumber';
import EnumsService from '../Services/enums.service';
import {
  HEBREW_MONTHS,
  round2,
  sumAgingRows,
} from './receivablesAging';

/** Match ExcelReport PALETTE for on-screen / PDF */
export const AGING_REPORT_COLORS = {
  primary: '#2F4BFF',
  primaryDark: '#1E308F',
  headerFont: '#FFFFFF',
  stripe: '#F6F8FF',
  plain: '#FFFFFF',
  totalFill: '#D7DFFF',
  text: '#1F2937',
  border: '#C3CBDB',
};

const formatDisplayDate = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateTimeNow = () => {
  const now = new Date();
  const time = now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const date = formatDisplayDate(now);
  return `${time} ${date}`;
};

const amountCell = (value) => {
  const n = round2(value);
  if (Math.abs(n) < 0.005) return '';
  return n;
};

/** Display amount like Excel UI (blank zeros, parentheses for negatives). */
const displayAmount = (value, { blankZero = true } = {}) => {
  const n = round2(value);
  if (blankZero && Math.abs(n) < 0.005) return '';
  return beautifyNumber(n);
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Build aging column headers (RTL visual order is handled by sheet RTL flag;
 * array order is logical LTR: id, name, phone, previous, months..., late, total).
 */
export const getAgingColumnHeaders = (t) => [
  t('CustomerNumber', 'מספר לקוח'),
  t('CustomerName', 'שם לקוח'),
  t('Phone', 'טלפון'),
  t('PreviousDebt', 'חוב קודם'),
  ...HEBREW_MONTHS.map((m) => `${t('Debt', 'חוב')} ${m}`),
  t('LateDebt', 'חוב מאוחר'),
  t('TotalDebt', 'סה"כ חוב'),
];

export const rowToAgingValues = (row) => [
  row.accountCardId,
  row.accountCardName,
  row.phoneNumber || '',
  amountCell(row.previous),
  ...row.months.map(amountCell),
  amountCell(row.late),
  amountCell(row.total),
];

/**
 * Excel export — same styling approach as AccountCardReportHTML downloadExcel
 * (xlsx-js-style, REPORT_STYLES, RTL, amount format).
 */
export const downloadReceivablesAgingExcel = ({
  rows,
  clientName,
  clientCase,
  caseTypeLabel,
  startDate,
  endDate,
  t = (k, d) => d || k,
}) => {
  const header = getAgingColumnHeaders(t);
  const titleLines = [
    `${caseTypeLabel || ''}: ${clientCase || ''}    ${clientName || ''}`.trim(),
    t('ReceivablesAgingByMonths', 'גיול חובות לפי חודשים'),
    `${t('FromDate', 'מתאריך')} ${formatDisplayDate(startDate)} ${t('ToDate', 'עד תאריך')} ${formatDisplayDate(endDate)}`,
  ];

  const dataRows = (rows || []).map(rowToAgingValues);
  const totals = sumAgingRows(rows);
  const totalRow = [
    t('Total', 'סה"כ'),
    '',
    '',
    amountCell(totals.previous),
    ...totals.months.map(amountCell),
    amountCell(totals.late),
    amountCell(totals.total),
  ];
  dataRows.push(totalRow);

  const numericColIndexes = Array.from({ length: 14 }, (_, i) => i + 3); // previous..total
  const colWidths = [14, 36, 16, 14, ...Array(12).fill(12), 14, 14];

  const colCount = header.length;
  const aoa = [];
  titleLines.forEach((line) => aoa.push([line]));
  aoa.push([]);
  const headerRowIndex = aoa.length;
  aoa.push(header);

  const sheetRows = dataRows.map((row) =>
    row.map((v, c) => {
      if (numericColIndexes.includes(c)) {
        if (v === '' || v === null || v === undefined) return '';
        return typeof v === 'number' ? v : round2(v);
      }
      return v ?? '';
    })
  );
  sheetRows.forEach((r) => aoa.push(r));

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!merges'] = titleLines.map((_, i) => ({
    s: { r: i, c: 0 },
    e: { r: i, c: Math.max(colCount - 1, 0) },
  }));
  ws['!cols'] = colWidths.map((wch) => ({ wch }));
  ws['!views'] = [{ RTL: true }];

  titleLines.forEach((_, i) => {
    const addr = XLSX.utils.encode_cell({ r: i, c: 0 });
    if (ws[addr]) {
      ws[addr].s = i === 1 ? REPORT_STYLES.reportTitle : i === 0 ? REPORT_STYLES.clientLine : REPORT_STYLES.subTitle;
    }
  });

  for (let c = 0; c < colCount; c += 1) {
    const addr = XLSX.utils.encode_cell({ r: headerRowIndex, c });
    if (!ws[addr]) ws[addr] = { t: 's', v: '' };
    ws[addr].s = REPORT_STYLES.header;
  }

  const totalRowIndex = sheetRows.length - 1;
  sheetRows.forEach((_, rowIdx) => {
    const r = headerRowIndex + 1 + rowIdx;
    const isTotal = rowIdx === totalRowIndex;
    const isStripe = !isTotal && rowIdx % 2 === 1;
    for (let c = 0; c < colCount; c += 1) {
      const addr = XLSX.utils.encode_cell({ r, c });
      let cell = ws[addr];
      if (!cell) {
        cell = { t: 's', v: '' };
        ws[addr] = cell;
      }
      const isNum = numericColIndexes.includes(c);
      if (isTotal) cell.s = isNum ? REPORT_STYLES.totalNum : REPORT_STYLES.totalText;
      else if (isStripe) cell.s = isNum ? REPORT_STYLES.stripeNum : REPORT_STYLES.stripeText;
      else cell.s = isNum ? REPORT_STYLES.dataNum : REPORT_STYLES.dataText;
      if (isNum && typeof cell.v === 'number') cell.z = AMOUNT_NUMBER_FORMAT;
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Aging');
  applyWorkbookRtl(wb);
  const safeName = (clientName || 'Client').replace(/[\\/:*?"<>|]/g, '_');
  XLSX.writeFile(wb, `${safeName}_ReceivablesAging.xlsx`);
};

/**
 * Full HTML document for PDF — same idea as AccountCardReportHTML print HTML
 * (server GeneratePdfFromHtml), Excel stripe/total colors, no amount clipping.
 */
export const buildReceivablesAgingPdfHtml = ({
  rows,
  clientName,
  clientBusinessLine,
  startDate,
  endDate,
  t = (k, d) => d || k,
}) => {
  const headers = getAgingColumnHeaders(t);
  const totals = sumAgingRows(rows);
  const title = t('ReceivablesAgingByMonths', 'גיול חובות לפי חודשים');
  const now = formatDateTimeNow();
  const startMs = startDate ? new Date(startDate).getTime() : 0;
  const endMs = endDate ? new Date(endDate).getTime() : 0;
  const periodStart = startMs <= endMs ? startDate : endDate;
  const periodEnd = startMs <= endMs ? endDate : startDate;
  const period = `${formatDisplayDate(periodStart)} – ${formatDisplayDate(periodEnd)}`;
  const C = AGING_REPORT_COLORS;

  // Month headers without "חוב" prefix — more room for amounts
  const pdfHeaders = headers.map((h, i) => {
    if (i >= 4 && i <= 15) return HEBREW_MONTHS[i - 4] || h;
    return h;
  });

  const bodyRows = (rows || [])
    .map((row, idx) => {
      const isStripe = idx % 2 === 1;
      const vals = [
        row.accountCardId,
        row.accountCardName,
        row.phoneNumber || '',
        displayAmount(row.previous),
        ...row.months.map((v) => displayAmount(v)),
        displayAmount(row.late),
        displayAmount(row.total, { blankZero: false }),
      ];
      return `<tr class="${isStripe ? 'stripe' : 'plain'}">${vals
        .map((v, i) => `<td class="${i >= 3 ? 'num' : 'txt'}">${escapeHtml(v ?? '')}</td>`)
        .join('')}</tr>`;
    })
    .join('');

  const totalVals = [
    t('Total', 'סה"כ'),
    '',
    '',
    displayAmount(totals.previous, { blankZero: false }),
    ...totals.months.map((v) => displayAmount(v, { blankZero: false })),
    displayAmount(totals.late, { blankZero: false }),
    displayAmount(totals.total, { blankZero: false }),
  ];
  const totalRowHtml = `<tr class="total">${totalVals
    .map((v, i) => `<td class="${i >= 3 ? 'num' : 'txt'}">${escapeHtml(v ?? '')}</td>`)
    .join('')}</tr>`;

  return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page {
      size: A4 landscape;
      margin: 8mm 6mm;
    }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, "Segoe UI", sans-serif;
      direction: rtl;
      color: ${C.text};
      margin: 0;
      padding: 6px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .top-bar {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;
      font-size: 11px;
      font-weight: 700;
    }
    .center-block { text-align: center; flex: 1; }
    h1 {
      margin: 2px 0 4px;
      font-size: 18px;
      color: ${C.primary};
      font-weight: 700;
      text-decoration: underline;
    }
    .meta { font-size: 11px; margin-bottom: 2px; }
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      font-size: 8px;
    }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    th, td {
      border: 1px solid ${C.border};
      padding: 3px 2px;
      vertical-align: middle;
      word-wrap: break-word;
    }
    th {
      background: ${C.primary} !important;
      color: ${C.headerFont} !important;
      font-weight: 700;
      text-align: center;
      font-size: 8px;
      line-height: 1.2;
    }
    td.txt { text-align: center; }
    td.num {
      text-align: center;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      font-weight: 600;
      font-size: 7.5px;
      letter-spacing: -0.02em;
    }
    tr.plain td { background: ${C.plain} !important; }
    tr.stripe td { background: ${C.stripe} !important; }
    tr.total td {
      font-weight: 700;
      background: ${C.totalFill} !important;
      color: ${C.primaryDark} !important;
      font-size: 8px;
    }
    tr.total td.num { font-size: 7.5px; }
  </style>
</head>
<body>
  <div class="top-bar">
    <div>${escapeHtml(now)}</div>
    <div class="center-block">
      <div class="meta">${escapeHtml(clientBusinessLine || clientName || '')}</div>
      <h1>${escapeHtml(title)}</h1>
      <div class="meta">${escapeHtml(period)}</div>
    </div>
    <div></div>
  </div>
  <table>
    <thead>
      <tr>${pdfHeaders.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr>
    </thead>
    <tbody>
      ${bodyRows}
      ${totalRowHtml}
    </tbody>
  </table>
</body>
</html>`;
};

/**
 * Download aging PDF via the same server pipeline as AccountCardReportHTML
 * (EnumsService.GeneratePdfFromHtml) — no html2canvas / broken page slicing.
 */
export const downloadReceivablesAgingPdf = async ({
  rows,
  clientName,
  clientCase,
  clientBusinessLine,
  startDate,
  endDate,
  token,
  t = (k, d) => d || k,
}) => {
  if (!token) throw new Error('Missing auth token');
  if (!clientCase) throw new Error('Missing case id');

  const html = buildReceivablesAgingPdfHtml({
    rows,
    clientName,
    clientBusinessLine,
    startDate,
    endDate,
    t,
  });

  const res = await EnumsService.GeneratePdfFromHtml(html, clientCase, token);
  if (!res?.ok) {
    const errText = await res?.text?.().catch(() => '');
    throw new Error(errText || 'PDF generation failed');
  }

  const contentType = res.headers.get('content-type') || '';
  let blob;

  if (contentType.includes('application/json')) {
    const responseData = await res.json();
    const pdfBase64 = responseData.data?.PdfBase64 || responseData.data?.pdfBase64 || responseData.PdfBase64;
    if (!pdfBase64) throw new Error('No PDF base64 in response');
    const binaryString = atob(pdfBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) bytes[i] = binaryString.charCodeAt(i);
    blob = new Blob([bytes], { type: 'application/pdf' });
  } else {
    blob = await res.blob();
  }

  const safeName = (clientName || 'Client').replace(/[\\/:*?"<>|]/g, '_');
  const fileName = `${safeName}-${clientCase || ''}_ReceivablesAging.pdf`.replace(/_+/g, '_');
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/** @deprecated use downloadReceivablesAgingPdf */
export const printReceivablesAgingPdf = downloadReceivablesAgingPdf;
